import { Tooltip } from '@mui/material'
import React, { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import uiConfigStore from 'store/uiConfig/uiConfig'
import { resetZoom } from 'store/zoom/zoom'
import { dynamicClassName } from 'styles/helper'
import Checkbox from 'ui/atoms/Checkbox/Checkbox'
import Icon from 'ui/atoms/Icon/Icon'
import ResizeableRow from 'ui/atoms/ResizeableRow/ResizeableRow'
import { getAllSingletonComponentNames, getComponentTitle } from 'ui/uiConfig/factory'
import { useSnapshot } from 'valtio'

import CreateNewUiConfig from 'views/UiConfigView/components/CreateNewUiConfig/CreateNewUiConfig'
import UiConfigBadge from 'views/UiConfigView/components/UiConfigBadge/UiConfigBadge'
import UiConfigVersions from './components/UiConfigVersions/UiConfigVersions'
import styles from './UiConfigPanel.module.less'

type FilterSection = 'Anchor components' | 'Components'

type SectionDescriptor = {
  key: FilterSection
  infoText: string
  includesComponent: (component: string, singletonComponentNames: Set<string>) => boolean
}

const SECTION_DESCRIPTORS: Record<FilterSection, SectionDescriptor> = {
  'Anchor components': {
    key: 'Anchor components',
    infoText:
      'Anchor components are UiConfigs that can only be used once per system. They have one dedicated placeholder in the UI, e.g followup_production which is only visible in /followupproduction.',
    includesComponent: (component, singletonComponentNames) => singletonComponentNames.has(component),
  },
  Components: {
    key: 'Components',
    infoText:
      'Components are UiConfigs that can be used multiple times per system. They are not visible unless added to a anchor component as a children_id.',
    includesComponent: (component, singletonComponentNames) => !singletonComponentNames.has(component),
  },
}

export default function UiConfigPanel(): ReactElement {
  const [showVersionForId, setShowVersionForId] = useState<UiConfigId | null>(null)
  const uiConfigSnap = useSnapshot(uiConfigStore)
  const { t } = useTranslation()
  const panelUids = uiConfigSnap.activeUids
  const uiConfigs = panelUids.map((uid) => uiConfigSnap.uiConfigs[uid]).reverse()
  const editingUiConfigChildrenUids = uiConfigSnap.editingUiConfigChildrenUids
  const singletonComponentNames = new Set(getAllSingletonComponentNames())
  const [filter, setFilter] = useState<FilterSection[]>(['Anchor components', 'Components'])

  function toggleFilterSection(section: FilterSection): void {
    if (filter.includes(section)) {
      setFilter(filter.filter((entry) => entry !== section))
    } else {
      setFilter([...filter, section])
    }
  }

  function selectUiConfig(uiConfig: UiConfig): void {
    if (typeof uiConfig.uid !== 'number') {
      return
    }

    resetZoom()
    if (uiConfig.id && uiConfigSnap.editingUid === uiConfig.uid) {
      toggleShowVersionForId(uiConfig.id)
      return
    }

    const didSwitch = uiConfigStore.setEditingUid(uiConfig.uid, {
      confirmDiscardDraft: () => window.confirm(t('Discard unsaved draft and switch to selected config?')),
    })

    if (!didSwitch) {
      return
    }

    setShowVersionForId(null)
  }

  function toggleShowVersionForId(id: UiConfigId): void {
    setShowVersionForId(showVersionForId === id ? null : id)
  }

  return (
    <div className={styles.UiConfigPanel}>
      <div style={{ display: 'flex' }}>
        <Checkbox
          label={t('Anchor components')}
          isChecked={filter.includes('Anchor components')}
          onClick={() => toggleFilterSection('Anchor components')}
        />
        <Checkbox
          label={t('Components')}
          isChecked={filter.includes('Components')}
          onClick={() => toggleFilterSection('Components')}
        />
      </div>
      {filter.map((sectionKey, index) => {
        const section = SECTION_DESCRIPTORS[sectionKey]
        const filteredUiConfigs = uiConfigs.filter(
          (uiConfig) =>
            !!uiConfig.component && section.includesComponent(uiConfig.component as string, singletonComponentNames)
        )

        return (
          <ResizeableRow height={`50%`} key={`${sectionKey}_${index}`}>
            <div className={styles.UiConfigPanel_Body} key={index}>
              <div className={styles.UiConfigPanel_Title}>
                {t(sectionKey) ?? sectionKey}
                <Tooltip title={t(section.infoText)} arrow>
                  <div>
                    <Icon icon="fas fa-info-circle" />
                  </div>
                </Tooltip>
              </div>
              {filteredUiConfigs.map((uiConfig) => {
                const uiConfigIsAnchorComponent = singletonComponentNames.has(uiConfig.component as string)

                return (
                  <div
                    key={uiConfig.uid}
                    className={dynamicClassName({
                      [styles.UiConfigPanel_Item]: true,
                      [styles.UiConfigPanel_Item__active]: uiConfig.id === uiConfigSnap.editingId,
                      [styles.UiConfigPanel_Item__highlight]:
                        !!uiConfig.id && editingUiConfigChildrenUids.has(uiConfig.id),
                      [styles.UiConfigPanel_Item__active__red]:
                        uiConfigIsAnchorComponent && uiConfig.id === uiConfigSnap.editingId,
                    })}
                  >
                    <div
                      className={dynamicClassName({
                        [styles.UiConfigPanel_Item_Text]: true,
                        [styles.UiConfigPanel_Item_Text__red]: uiConfigIsAnchorComponent,
                      })}
                      onClick={() => selectUiConfig(uiConfig)}
                    >
                      {uiConfigIsAnchorComponent ? (
                        <>
                          {`${uiConfig.id}.${uiConfig.version} ${uiConfig.display_name || getComponentTitle(uiConfig.component as string)}`}
                          <span
                            style={{ fontStyle: 'italic', fontSize: 'small', fontWeight: 'lighter' }}
                          >{` ${uiConfig.component}`}</span>
                        </>
                      ) : (
                        <>
                          {`${uiConfig.id}.${uiConfig.version} ${uiConfig.display_name || ''}`}
                          <span
                            style={{ fontStyle: 'italic', fontSize: 'small', fontWeight: 'lighter' }}
                          >{` ${getComponentTitle(uiConfig.component as string)}`}</span>
                        </>
                      )}

                      <UiConfigBadge uiConfig={uiConfig} />
                    </div>

                    {uiConfig.id === showVersionForId && <UiConfigVersions />}
                  </div>
                )
              })}
            </div>
          </ResizeableRow>
        )
      })}
      <CreateNewUiConfig />
    </div>
  )
}
